/**
 * User form: shared domain model.
 *
 * Pure, framework-free types shared by the `add-user` and `edit-user` slices.
 * Both edit the same identity (first name, last name, email, and since #591 an
 * optional LinkedIn profile URL);
 * the licence dimension differs (Add picks one, Edit shows it read-only), so
 * `UserDraft` holds only the genuinely-common part. No React, no Redux, no fetch.
 *
 * This module depends on NEITHER feature (hermeticity) and never reaches into the
 * sibling `administrator` domain or any client feature.
 */

/** The editable identity fields shared by Add and Edit. */
export interface UserDraft {
  firstName: string;
  lastName: string;
  email: string;
  /** Optional: `''` = none. Sent normalised, `null` when empty (#591). */
  linkedinUrl: string;
}

/** A pristine draft: the starting point for the Edit form / base of Add. */
export const emptyDraft: UserDraft = {
  firstName: '',
  lastName: '',
  email: '',
  linkedinUrl: '',
};

/** Field-keyed validation messages produced by `validateDraft`. */
export type ValidationErrors = Partial<Record<keyof UserDraft, string>>;

/** Lifecycle of a save (create / update / delete). */
export type SaveStatus = 'idle' | 'saving' | 'succeeded' | 'failed';

/** Lifecycle of an initial load (Edit hydrates from the backend). */
export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';
